// The selection language, in one place.
//
// SELECTION IS NOT AN ACTION. `ui/Button.tsx` owns the action ladder (primary
// / secondary / ghost / danger) and spends no hue; this owns everything that
// says "you picked this one" and spends no hue either. Between them the
// accent is left free to mean exactly one thing -- where you are in the flow,
// per design-language.md section 1 -- and the blue stays on the acoustic data
// where the palette doc puts it.
//
// This exists because the same two shapes were hand-written eight times
// across the guided flow and had already drifted apart: option cards in both
// `rounded-sm` and `rounded-lg`, glyphs in both `rounded-sm` and
// `rounded-full`, and a selected state that was `accent-soft` in five places
// and solid `accent` in three. Every one of those was somebody copying a
// neighbour and adjusting. A recipe they all call cannot drift.
//
// Selected reads as RAISED, not tinted: the brightest surface, a brighter
// hairline, an ink label, and a real shadow. The shadow is load-bearing --
// raised-vs-recessed is only a 1.21 contrast ratio on this palette (the
// accent fill it replaces was 4.30), so fill alone cannot carry the state.
// What carries it is the label jumping from --ed-text-dim to --ed-text (3.78
// to 15.56 against the surface behind it) plus genuine elevation.
//
// Every `--ed-*` token below names its colour twice -- `var(--ed-x,var(--rt-y))`
// -- the same recipe `ui/Button.tsx` uses and for the same reason: `--ed-*` is
// defined on `.guided-theme`, not `:root` (globals.css), so outside the editor
// this recipe used to paint an unstyled border and inherited text. The
// fallback is the very token the alias points at, so the two halves are the
// same colour by construction and this is safe to call from a marketing page
// without wrapping it in `.guided-theme` -- which would pull in the editor's
// whole chrome to borrow one colour ramp.

/** Ink at low alpha, the same recipe `.ed-range`'s thumb uses in globals.css.
 *  Shared with `Segmented` so there is one elevation in the editor, not two. */
export const RAISED_SHADOW =
  'shadow-[0_1px_2px_color-mix(in_srgb,var(--rt-shadow)_16%,transparent)]';

/**
 * Just the STATE half of the selection language, without any geometry.
 *
 * Density legitimately varies -- a coverage card is 12px with a caption, a
 * panel filter is 11px, a chart-header smoothing toggle is 9.5px and has to
 * fit beside a chart title. Forcing all of them into one padding would break
 * layouts to satisfy a rule about colour. What must NOT vary is what
 * "selected" looks like, so that is what this exports: callers keep their own
 * radius, padding and type size and take the state from here.
 */
export function selectedStateClass(active: boolean): string {
  return active ? SELECTED_RAISED : IDLE;
}

/** The selected half on its own, for the ternaries whose idle branch is
 *  genuinely file-specific (a disabled tier, a faint chart-header rest state)
 *  and should not be flattened into the shared one. */
export const SELECTED_RAISED =
  'border-[var(--ed-border-bright,var(--rt-hairline-strong))] '
  + `bg-[var(--ed-input,var(--rt-raised))] text-[var(--ed-text,var(--rt-ink))] ${RAISED_SHADOW}`;

const IDLE =
  'border-[var(--ed-border,var(--rt-hairline))] text-[var(--ed-text-dim,var(--rt-ink-dim))] '
  + 'hover:border-[var(--ed-border-bright,var(--rt-hairline-strong))] hover:text-[var(--ed-text,var(--rt-ink))]';

const CARD_BASE = 'flex gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors';
/** A tile's frame is its picture's mat, not a gutter — see `optionTileClass`. */
const TILE_BASE = 'flex min-w-0 flex-col gap-1.5 rounded-lg border p-1.5 text-left transition-colors';

function optionState(active: boolean): string {
  return active
    ? 'border-[var(--ed-border-bright,var(--rt-hairline-strong))] '
      + `bg-[var(--ed-input,var(--rt-raised))] ${RAISED_SHADOW}`
    // Hover presses IN (--ed-surface-2 is the recessed step) while selected
    // lifts OUT. Opposite directions, so "I am hovering this" can never be
    // mistaken for "I have chosen this".
    : 'border-[var(--ed-border,var(--rt-hairline))] '
      + 'hover:border-[var(--ed-border-bright,var(--rt-hairline-strong))] hover:bg-[var(--ed-surface-2,var(--rt-hover))]';
}

/**
 * A pickable option row -- the big card with a glyph and a label.
 * Pass `extra` for layout only (`items-start` / `items-center`), never colour.
 */
export function optionCardClass(active: boolean, extra = ''): string {
  return [CARD_BASE, optionState(active), extra].filter(Boolean).join(' ');
}

/**
 * A pickable tile -- a picture (a drawn room, a thumbnail, a glyph) over its
 * label, in a grid. The card's states on a tighter frame: a tile's picture is
 * its content, so the padding only mats it. Its own base rather than
 * `optionCardClass` plus a padding override, because nothing here merges
 * classes -- a second `p-*` beside the card's `px-3 py-2.5` wins or loses by
 * stylesheet order, and three tiles had been passing one that lost.
 * Pass `extra` for layout only (`items-center`, `text-center`, a min height).
 */
export function optionTileClass(active: boolean, extra = ''): string {
  return [TILE_BASE, optionState(active), extra].filter(Boolean).join(' ');
}

/**
 * The tick inside an option card. `box` for multi-select, `dot` for
 * single-select -- the shape is the only thing that says which, so it has to
 * match the card's actual behaviour.
 *
 * Filled with ink rather than accent. A filled tick IS the checkbox
 * convention, so it stays filled; `--ed-ink` is the label-on-an-ink-fill
 * token, which makes this the pairing it was named for.
 */
export function optionGlyphClass(active: boolean, shape: 'box' | 'dot' = 'box'): string {
  const base =
    'flex h-4 w-4 shrink-0 items-center justify-center border '
    + (shape === 'dot' ? 'rounded-full' : 'rounded-sm');
  const state = active
    ? 'border-[var(--ed-text,var(--rt-ink))] bg-[var(--ed-text,var(--rt-ink))] text-[var(--ed-ink,var(--rt-button-label))]'
    : 'border-[var(--ed-border-bright,var(--rt-hairline-strong))]';
  return `${base} ${state}`;
}

/**
 * A standalone toggle chip, for a choice too small to deserve a card and not
 * mutually exclusive enough for `Segmented`.
 *
 * Idle is deliberately unfilled: if idle carried `--ed-input` too, the raised
 * selected state would have nothing to be raised ABOVE.
 */
export function optionChipClass(
  active: boolean,
  { disabled = false }: { disabled?: boolean } = {},
): string {
  const base = 'rounded-lg border px-3.5 py-2 text-body-sm font-medium transition-colors';
  if (disabled) {
    return `${base} cursor-not-allowed border-[var(--ed-border,var(--rt-hairline))] `
      + 'text-[var(--ed-text-faint,var(--rt-ink-faint))] opacity-40';
  }
  return `${base} ${selectedStateClass(active)}`;
}
